import React from "react";
import LoadingButton from "../../components/LoadingButton";
import { retentionInstallmentMin } from "../../utils/offerDateRules";

/*
 * Shared Offer Editor
 *
 * This component intentionally renders the same offer data in every stage,
 * but only fields listed in editableFields are enabled.
 *
 * IMPORTANT:
 * - Frontend controls the UI.
 * - Backend must independently validate role + stage + allowed fields.
 */

export const CANDIDATE_FIELDS = [
  "candidate_name",
  "position_offered",
  "salutation",
  "email",
  "mobile",
  "experience",
  "relevant_experience",
  "address",
  "notice_period",
  "hiring_manager_name",
  "hiring_manager_email",
];

/*
 * Groups the Create Offer modal puts behind `offer_type === "regular"`. The
 * editor must show the same sections for the same offer type — otherwise an
 * approver sees fields that were never collected, and a conditional offer shows
 * an empty single-salary grid that will never be saved.
 */
const REGULAR_ONLY = (offerType) => offerType !== "conditional";
const CONDITIONAL_ONLY = (offerType) => offerType === "conditional";

// Derived totals. They are computed on save, never typed, and no edit role may
// change them — rendering them as inputs would invite an edit the backend drops.
const COMPUTED_FIELDS = new Set([
  "current_ctc_total",
  "expected_ctc_total",
  "offered_ctc_total",
  "offered_ctc_gross_total",
]);

export const ALL_OFFER_FIELDS = [
  ...CANDIDATE_FIELDS,

  "business_unit",
  "division",
  "department",

  "current_location",
  "current_company",
  "location_offered",
  "offered_company",
  "work_mode",
  "expected_doj",
  "reporting_manager",

  "current_ctc_fixed",
  "current_ctc_variable",
  "current_ctc_variable_amount",
  "current_ctc_total",

  "expected_ctc_fixed",
  "expected_ctc_variable",
  "expected_ctc_variable_amount",
  "expected_ctc_total",

  "offered_ctc_fixed",
  "offered_ctc_variable",
  "offered_ctc_variable_amount",
  "offered_ctc_total",
  "offered_ctc_gross_total",
  "variable_annual_pay_amount",
  "variable_retention_bonus_amount",

  "joining_bonus",
  "joining_bonus_amount",

  "retention_bonus",
  "retention_total",
  "retention_installment1_date",
  "retention_installment1_amount",
  "retention_installment2_date",
  "retention_installment2_amount",
  "retention_installment3_date",
  "retention_installment3_amount",

  "training_period",
  "training_amount",
  "internship_period",
  "internship_stipend",

  "year1_fixed",
  "year1_variable_type",
  "year1_variable_amount",
  "year1_ctc",

  "year2_fixed",
  "year2_variable_type",
  "year2_variable_amount",
  "year2_ctc",

  "year3_fixed",
  "year3_variable_type",
  "year3_variable_amount",
  "year3_ctc",

  "year4_fixed",
  "year4_variable_type",
  "year4_variable_amount",
  "year4_ctc",

  "service_agreement",
  "offer_type",
  "company",
  "templateType",
];

const GROUPS = [
  {
    title: "Candidate Information",
    fields: CANDIDATE_FIELDS,
  },
  {
    title: "Organization Details",
    fields: [
      "business_unit",
      "division",
      "department",
    ],
  },
  {
    title: "Location & Work Details",
    fields: [
      "current_location",
      "current_company",
      "location_offered",
      "offered_company",
      "work_mode",
"expected_doj",
  "reporting_manager",
    ],
  },
  {
    title: "Current CTC",
    when: REGULAR_ONLY,
    fields: [
      "current_ctc_fixed",
      "current_ctc_variable",
      "current_ctc_variable_amount",
      "current_ctc_total",
    ],
  },
  {
    title: "Expected CTC",
    when: REGULAR_ONLY,
    fields: [
      "expected_ctc_fixed",
      "expected_ctc_variable",
      "expected_ctc_variable_amount",
      "expected_ctc_total",
    ],
  },
  {
    title: "Offered CTC",
    when: REGULAR_ONLY,
    fields: [
      "offered_ctc_fixed",
      "offered_ctc_variable",
      "offered_ctc_variable_amount",
      "variable_annual_pay_amount",
      "variable_retention_bonus_amount",
      "offered_ctc_total",
      "offered_ctc_gross_total",
    ],
  },
  {
    title: "Joining Bonus",
    when: REGULAR_ONLY,
    fields: [
      "joining_bonus",
      "joining_bonus_amount",
    ],
  },
  {
    title: "Retention Bonus",
    when: REGULAR_ONLY,
    fields: [
      "retention_bonus",
      "retention_total",
      "retention_installment1_date",
      "retention_installment1_amount",
      "retention_installment2_date",
      "retention_installment2_amount",
      "retention_installment3_date",
      "retention_installment3_amount",
    ],
  },
  {
    title: "Grade & Designation",
    when: REGULAR_ONLY,
    fields: [
      "grade",
      "designation",
    ],
  },
  {
    title: "Conditional Offer",
    when: CONDITIONAL_ONLY,
    fields: [
      "training_period",
      "training_amount",
      "internship_period",
      "internship_stipend",
      "year1_fixed",
      "year1_variable_type",
      "year1_variable_amount",
      "year1_ctc",
      "year2_fixed",
      "year2_variable_type",
      "year2_variable_amount",
      "year2_ctc",
      "year3_fixed",
      "year3_variable_type",
      "year3_variable_amount",
      "year3_ctc",
      "year4_fixed",
      "year4_variable_type",
      "year4_variable_amount",
      "year4_ctc",
    ],
  },
  // {
  //   title: "Offer Configuration",
  //   fields: [
  //     "service_agreement",
  //     "offer_type",
  //     "company",
  //     "templateType",
  //   ],
  // },
];

const LABELS = {
  candidate_name: "Candidate Name",
  position_offered: "Position Offered",
  salutation: "Salutation",
  email: "Candidate Email",
  mobile: "Candidate Mobile",
  experience: "Experience",
  relevant_experience: "Relevant Experience",
  grade: "Grade",
  designation: "Designation / Title",
  address: "Address",
  notice_period: "Notice Period",
  hiring_manager_name: "Hiring Manager",
  hiring_manager_email: "Hiring Manager Email",

  business_unit: "Business Unit",
  division: "Division",
  department: "Department",

  current_location: "Current Location",
  current_company: "Current Company",
  location_offered: "Offered Location",
  offered_company: "Offered Company",
  work_mode: "Work Mode",
  expected_doj: "Expected DOJ",
  reporting_manager: "Reporting Manager",
  bill_rate_per_month: "Bill Rate / Month",

  current_ctc_fixed: "Current Fixed CTC",
  current_ctc_variable: "Current Variable",
  current_ctc_variable_amount: "Current Variable Amount",
  current_ctc_total: "Current Total CTC",

  expected_ctc_fixed: "Expected Fixed CTC",
  expected_ctc_variable: "Expected Variable",
  expected_ctc_variable_amount: "Expected Variable Amount",
  expected_ctc_total: "Expected Total CTC",

  offered_ctc_fixed: "Offered Fixed CTC",
  offered_ctc_variable: "Offered Variable",
  offered_ctc_variable_amount: "Offered Variable Amount",
  offered_ctc_total: "Offered Total CTC",
  offered_ctc_gross_total: "Offered Total CTC (incl. benefits)",
  variable_annual_pay_amount: "Annual Variable Pay (p.a.)",
  variable_retention_bonus_amount: "Annual Retention Bonus (p.a.)",

  joining_bonus: "Joining Bonus",
  joining_bonus_amount: "Joining Bonus Amount",

  retention_bonus: "Retention Bonus",
  retention_total: "Retention Total",
  retention_installment1_date: "Retention Installment 1 Date",
  retention_installment1_amount: "Retention Installment 1 Amount",
  retention_installment2_date: "Retention Installment 2 Date",
  retention_installment2_amount: "Retention Installment 2 Amount",
  retention_installment3_date: "Retention Installment 3 Date",
  retention_installment3_amount: "Retention Installment 3 Amount",

  training_period: "Training Period",
  training_amount: "Training Amount",
  internship_period: "Internship Period",
  internship_stipend: "Internship Stipend",

  year1_fixed: "Year 1 Fixed",
  year1_variable_type: "Year 1 Variable Type",
  year1_variable_amount: "Year 1 Variable Amount",
  year1_ctc: "Year 1 CTC",

  year2_fixed: "Year 2 Fixed",
  year2_variable_type: "Year 2 Variable Type",
  year2_variable_amount: "Year 2 Variable Amount",
  year2_ctc: "Year 2 CTC",

  year3_fixed: "Year 3 Fixed",
  year3_variable_type: "Year 3 Variable Type",
  year3_variable_amount: "Year 3 Variable Amount",
  year3_ctc: "Year 3 CTC",

  year4_fixed: "Year 4 Fixed",
  year4_variable_type: "Year 4 Variable Type",
  year4_variable_amount: "Year 4 Variable Amount",
  year4_ctc: "Year 4 CTC",

  service_agreement: "Service Agreement",
  offer_type: "Offer Type",
  company: "Company",
  templateType: "Template Type",
};

// Mirrors the Create Offer modal: the variable cadence is stored with the
// "Bonus" suffix, so a value written there must round-trip through this list.
const VARIABLE_TYPE_OPTIONS = [
  { value: "", label: "Select" },
  { value: "Monthly Bonus", label: "Monthly Bonus" },
  { value: "Quarterly Bonus", label: "Quarterly Bonus" },
  { value: "Half Yearly Bonus", label: "Half Yearly Bonus" },
  { value: "Yearly Bonus", label: "Yearly Bonus" },
];

const SELECT_OPTIONS = {
  salutation: [
    { value: "", label: "Select" },
    { value: "Mr.", label: "Mr." },
    { value: "Ms.", label: "Ms." },
    { value: "Mrs.", label: "Mrs." },
    { value: "Dr.", label: "Dr." },
  ],
  grade: [
    { value: "", label: "Select" },
    { value: "A", label: "A" },
    { value: "B", label: "B" },
    { value: "C", label: "C" },
    { value: "D", label: "D" },
    { value: "E", label: "E" },
  ],
  joining_bonus: [
    { value: "No", label: "No" },
    { value: "Yes", label: "Yes" },
  ],
  retention_bonus: [
    { value: "No", label: "No" },
    { value: "Yes", label: "Yes" },
  ],
  work_mode: [
    { value: "", label: "Select" },
    { value: "Work from Office", label: "Work from Office" },
    { value: "Remote", label: "Remote" },
    { value: "Hybrid", label: "Hybrid" },
  ],
  // These must stay identical to the Create Offer modal's option lists. A stored
  // value with no matching option renders as blank, and saving then writes NULL.
  offered_ctc_variable: VARIABLE_TYPE_OPTIONS,
  current_ctc_variable: VARIABLE_TYPE_OPTIONS,
  expected_ctc_variable: VARIABLE_TYPE_OPTIONS,
  year1_variable_type: VARIABLE_TYPE_OPTIONS,
  year2_variable_type: VARIABLE_TYPE_OPTIONS,
  year3_variable_type: VARIABLE_TYPE_OPTIONS,
  year4_variable_type: VARIABLE_TYPE_OPTIONS,
  offer_type: [
    { value: "regular", label: "Regular" },
    { value: "conditional", label: "Conditional" },
  ],
  service_agreement: [
    { value: "", label: "Select" },
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ],
  company: [
    { value: "uss", label: "Univision Systems & Solutions" },
    { value: "eldaas", label: "Eldaas Technologies" },
  ],
  templateType: [
    { value: "", label: "Select" },
    { value: "with_agreement", label: "With Agreement" },
    { value: "without_agreement", label: "Without Agreement" },
    { value: "service_agg", label: "Service Agreement" },
    { value: "regular", label: "Regular" },
  ],
};

const SELECT_FIELDS = new Set([
  "salutation",
  "grade",
  "joining_bonus",
  "retention_bonus",
  "work_mode",
  "current_ctc_variable",
  "expected_ctc_variable",
  "offered_ctc_variable",
  "year1_variable_type",
  "year2_variable_type",
  "year3_variable_type",
  "year4_variable_type",
  "offer_type",
  "service_agreement",
  "company",
  "templateType",
  "business_unit",
  "division",
  "department",
]);

const DATE_FIELDS = new Set([
  "expected_doj",
  "retention_installment1_date",
  "retention_installment2_date",
  "retention_installment3_date",
]);

// Installment fields in schedule order, so each one can be floored to the
// previous installment and out-of-order days are not selectable.
const INSTALLMENT_FIELD_INDEX = {
  retention_installment1_date: 0,
  retention_installment2_date: 1,
  retention_installment3_date: 2,
};

// Earliest day the picker should allow for a date field. Retention installments
// chain off the previous installment. expected_doj is deliberately NOT floored
// here: on the Edit screens an offer can legitimately already be past its
// joining date, and it must stay editable.
function dateFieldMin(field, form) {
  const index = INSTALLMENT_FIELD_INDEX[field];
  if (index === undefined) return undefined;
  return retentionInstallmentMin(index, form);
}

const NUMBER_FIELDS = new Set([
  "experience",
  "relevant_experience",
  "current_ctc_fixed",
  "current_ctc_variable_amount",
  "current_ctc_total",
  "expected_ctc_fixed",
  "expected_ctc_variable_amount",
  "expected_ctc_total",
  "offered_ctc_fixed",
  "offered_ctc_variable_amount",
  "offered_ctc_total",
  "offered_ctc_gross_total",
  "variable_annual_pay_amount",
  "variable_retention_bonus_amount",
  "joining_bonus_amount",
  "retention_total",
  "retention_installment1_amount",
  "retention_installment2_amount",
  "retention_installment3_amount",
  "training_amount",
  "internship_stipend",
  "year1_fixed",
  "year1_variable_amount",
  "year1_ctc",
  "year2_fixed",
  "year2_variable_amount",
  "year2_ctc",
  "year3_fixed",
  "year3_variable_amount",
  "year3_ctc",
  "year4_fixed",
  "year4_variable_amount",
  "year4_ctc",
]);

const TEXTAREA_FIELDS = new Set([
  "address",
]);

function normaliseValue(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function Field({
  field,
  form,
  setForm,
  editable,
  options = {},
}) {
  const value = normaliseValue(form?.[field]);
  const label = LABELS[field] || field;

  const handleChange = (event) => {
    setForm((previous) => ({
      ...previous,
      [field]: event.target.value,
    }));
  };

  const commonProps = {
    value,
    onChange: handleChange,
    // A computed total is never editable, whatever the role's field list says.
    disabled: !editable,
    readOnly: COMPUTED_FIELDS.has(field),
  };

  if (TEXTAREA_FIELDS.has(field)) {
    return (
      <div className="form-group">
        <label>{label}</label>
        <textarea
          {...commonProps}
          rows={2}
          placeholder={label}
        />
      </div>
    );
  }

  if (SELECT_FIELDS.has(field)) {
    let fieldOptions = SELECT_OPTIONS[field] || [];

    if (field === "business_unit" && options.businessUnits) {
      fieldOptions = [
        { value: "", label: "Select" },
        ...options.businessUnits.map((item) => ({
          value: item.value ?? item,
          label: item.label ?? item.name ?? item.value ?? item,
        })),
      ];
    }

    if (field === "division" && options.divisions) {
      fieldOptions = [
        { value: "", label: "Select" },
        ...options.divisions.map((item) => ({
          value: item.value ?? item,
          label: item.label ?? item.name ?? item.value ?? item,
        })),
      ];
    }

    if (field === "department" && options.departments) {
      fieldOptions = [
        { value: "", label: "Select" },
        ...options.departments.map((item) => ({
          value: item.value ?? item,
          label: item.label ?? item.name ?? item.value ?? item,
        })),
      ];
    }

    return (
      <div className="form-group">
        <label>{label}</label>
        <select {...commonProps}>
          {fieldOptions.length ? (
            fieldOptions.map((item) => (
              <option key={`${field}-${item.value}`} value={item.value}>
                {item.label}
              </option>
            ))
          ) : (
            <option value="">{value || "Not configured"}</option>
          )}
        </select>
      </div>
    );
  }

  return (
    <div className="form-group">
      <label>{label}</label>
      <input
        {...commonProps}
        type={
          DATE_FIELDS.has(field)
            ? "date"
            : NUMBER_FIELDS.has(field)
            ? "number"
            : field === "email" || field === "hiring_manager_email"
            ? "email"
            : "text"
        }
        min={dateFieldMin(field, form)}
        placeholder={label}
      />
    </div>
  );
}

export default function OfferEditorCommon({
  title = "Edit Offer",
  stageLabel = "",
  form,
  setForm,
  editableFields = [],
  onSave,
  onCancel,
  saving = false,
  headcountOptions = {},
  showAllFields = true,
  note = "",
  isReturnedOffer = false,
  onSaveAndSend,
  rejectionBanner = null,
}) {
  const editableSet = new Set(editableFields);

  // The editor mirrors the Create Offer modal: an offer initiated as a regular
  // offer shows the compensation grid, grade/designation and bonuses; one
  // initiated as conditional shows the training/internship and Year 1-4 pay
  // schedule. Neither type sees the other's fields.
  const offerType = String(form?.offer_type || "regular").trim() || "regular";

  const groups = GROUPS.filter((group) => {
    if (group.when && !group.when(offerType)) return false;
    if (showAllFields) return true;
    return group.fields.some((field) => editableSet.has(field));
  });

  return (
    <div className="modal-overlay">
      <div className="modal-content create-offer-modal-wide">
        <div className="offer-editor-header">
          <h3>{title}</h3>
          {stageLabel && (
            <span className="offer-editor-stage">
              {stageLabel}
            </span>
          )}
        </div>

        {note && (
          <div className="offer-editor-note">
            {note}
          </div>
        )}

        {/* The offer type decides which sections exist above, so say it out loud:
            an approver must not read the missing fields as a loading bug. */}
        <div className="offer-editor-note">
          Offer type: <strong>{offerType === "conditional" ? "Conditional" : "Regular"}</strong>
          {offerType === "conditional"
            ? " — this offer was initiated as a conditional offer, so only the training / internship and Year 1-4 pay schedule fields are shown."
            : " — this offer was initiated as a regular offer, so only the compensation, bonus and grade fields are shown."}
          {" The offer type cannot be changed after initiation."}
        </div>

        {rejectionBanner && (
          <div className="edit-modal-rejection-banner">
            {rejectionBanner}
          </div>
        )}

        <form
          onSubmit={(event) => {
            event.preventDefault();

            if (
              isReturnedOffer &&
              typeof onSaveAndSend === "function"
            ) {
              onSaveAndSend();
              return;
            }

            if (typeof onSave === "function") {
              onSave();
            }
          }}
        >
          {groups.map((group) => (
            <div
              className="form-section"
              key={group.title}
            >
              <h4>{group.title}</h4>

              <div className="form-row">
                {group.fields.map((field) => (
                  <Field
                    key={field}
                    field={field}
                    form={form}
                    setForm={setForm}
                    editable={editableSet.has(field)}
                    options={headcountOptions}
                  />
                ))}
              </div>
            </div>
          ))}

          <div className="modal-actions">
            {editableFields.length > 0 && (
              isReturnedOffer &&
              typeof onSaveAndSend === "function" ? (
                <LoadingButton
                  type="submit"
                  className="primary-btn"
                  loading={saving}
                  loadingText="Updating & Sending..."
                >
                  Update Offer & Send
                </LoadingButton>
              ) : (
                <LoadingButton
                  type="submit"
                  className="primary-btn"
                  loading={saving}
                  loadingText="Updating..."
                >
                  Update Offer
                </LoadingButton>
              )
            )}

            <button
              type="button"
              className="secondary-btn"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
